import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NavBar from "../../components/common/NavBar";
import { useLocation, useParams } from "react-router-dom";
import TeamHeader from "../../components/team/TeamHeader";
import PlayersList from "../../components/team/PlayersList";
import InviteTeamLink from "../../components/team/InviteTeamLink";
import useTranslation from "../../i18n/useTranslation";
import TeamCoaches from "../../components/team/TeamCoaches";
import NoData from "../../components/common/NoData";
import GetDivisionDetail from "../../components/division/queries/GetDivisionDetail";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import AddTeamMemberModal from "../../components/team/AddTeamMemberModal";
import CreateOrganizationUserModal from "../../components/organization/CreateOrganizationUserModal";

function normalizeSlug(value) {
    return decodeURIComponent(value || "").trim().toLowerCase();
}

function TeamDetailPage() {
    const params = useParams();
    const location = useLocation();
    const { t } = useTranslation();
    const { activeProfile } = useActiveProfile();
    const themeColors = useThemeColors();
    const [getDivisionDetails] = GetDivisionDetail();

    const initialState = location.state || {};
    const initialTeamId = initialState?.team?.teamId || null;
    const seasonSelectionKey = `${activeProfile?.id}:${params.divSlug}`;
    const selectedSeasonSlug = useSelector(
        (state) => state.user.activeSeasonSelections?.[seasonSelectionKey]
    );
    const [activeSeasonData, setActiveSeasonData] = useState(initialState.activeSeasonData || null);
    const [division, setDivision] = useState(initialState.division || null);
    const [team, setTeam] = useState(initialState.team || null);
    const [inviteRole, setInviteRole] = useState(null);
    const [createUserRole, setCreateUserRole] = useState(null);
    const loadedDivisionKeyRef = useRef(null);
    const colors = useMemo(() => initialState.colors || themeColors, [initialState.colors, themeColors]);
    const hasEnterpriseUserManagement = division?.organization?.activeSubscription?.plan?.features?.some(
        (feature) => feature?.key === "enterprise_user_management"
    );

    const requireEnterpriseUserManagement = () => {
        if (hasEnterpriseUserManagement) return true;
        toast.warning(t("subscriptionLimit.feature_required"), {
            autoClose: 5000,
            position: "top-center",
            closeOnClick: true,
        });
        return false;
    };

    useEffect(() => {
        if (!params.divSlug || !params.teamSlug || !activeProfile?.id) return;

        const requestKey = [
            activeProfile.id,
            params.divSlug,
            params.teamSlug,
            selectedSeasonSlug || initialState.activeSeasonData?.slug || "",
        ].join(":");
        if (loadedDivisionKeyRef.current === requestKey) return;
        loadedDivisionKeyRef.current = requestKey;

        getDivisionDetails({
            variables: {
                divisionSlug: params.divSlug,
                profileId: activeProfile.id,
            },
            fetchPolicy: "network-only",
        })
            .then((res) => {
                const divisionData = res?.data?.division;
                if (!divisionData) return;

                const requestedSeasonSlug = selectedSeasonSlug || initialState.activeSeasonData?.slug;
                const season = divisionData.seasons?.find(
                    (item) => item.slug === requestedSeasonSlug
                ) || divisionData.activeSeason || null;
                const targetSlug = normalizeSlug(params.teamSlug);
                const seasonTeamBySlug =
                    season?.teams?.find(
                        (item) => normalizeSlug(item?.team?.slug) === targetSlug
                    ) || null;
                const seasonTeamById =
                    season?.teams?.find(
                        (item) => Number(item?.teamId) === Number(initialTeamId)
                    ) || null;
                const resolvedTeam = seasonTeamBySlug || seasonTeamById || null;

                setActiveSeasonData(season);
                setDivision(divisionData);
                setTeam(resolvedTeam);
            })
            .catch(() => {
                // Keep route state if fetch fails.
                loadedDivisionKeyRef.current = null;
            });
    }, [
        activeProfile?.id,
        getDivisionDetails,
        initialState.activeSeasonData,
        initialState.team,
        initialTeamId,
        params.divSlug,
        params.teamSlug,
        selectedSeasonSlug,
    ]);

    const navBarElem = {
        pageTitle: t("teamDetailPage.pageTitle"),
        links: [
            {
                pageTitle: t("teamDetailPage.breadcrumbOrganizations"),
                navLink: "/management/organizations",
                active: false
            },
            {
                pageTitle: `${division?.organization?.name} - ${t("teamDetailPage.breadcrumbDivisions")}`,
                navLink: `/management/organizations/${params.orgSlug}/divisions`,
                active: false
            },
            {
                pageTitle: `${division?.name}`,
                navLink: `/management/organizations/${params.orgSlug}/divisions/${params.divSlug}`,
                active: false
            },
            {
                pageTitle: `${team?.teamName}`,
                navLink: null,
                active: true
            }
        ]
    };

    const handlePlayerUpdated = (rosterId, updates) => {
        setTeam((prev) => {
            if (!prev?.team?.players?.length) return prev;

            return {
                ...prev,
                team: {
                    ...prev.team,
                    players: prev.team.players.map((player) =>
                        player.id === rosterId ? { ...player, ...updates } : player
                    ),
                },
            };
        });
    };

    const handlePlayerDeleted = (rosterId) => {
        setTeam((prev) => {
            if (!prev?.team) return prev;

            return {
                ...prev,
                team: {
                    ...prev.team,
                    players: (prev.team.players || []).filter((player) => player.id !== rosterId),
                },
            };
        });
    };

    const handleCoachDeleted = (rosterId) => {
        setTeam((prev) => {
            if (!prev?.team) return prev;

            return {
                ...prev,
                team: {
                    ...prev.team,
                    coaches: (prev.team.coaches || []).filter((coach) => coach.id !== rosterId),
                },
            };
        });
    };

    const closeInviteModal = () => setInviteRole(null);
    const closeCreateUserModal = () => setCreateUserRole(null);

    const openAddCoachModal = () => {
        if (!team?.teamId || !activeSeasonData?.id) return;
        setInviteRole(2);
    };

    const openAddPlayerModal = () => {
        if (!team?.teamId || !activeSeasonData?.id) return;
        setInviteRole(3);
    };

    const existingProfileIds = useMemo(() => {
        if (Number(inviteRole) === 2) {
            return team?.team?.coaches?.map((coach) => Number(coach?.playerId)).filter(Boolean) || [];
        }

        if (Number(inviteRole) === 3) {
            return team?.team?.players?.map((player) => Number(player?.playerId)).filter(Boolean) || [];
        }

        return [];
    }, [inviteRole, team?.team?.coaches, team?.team?.players]);

    const handleMemberAdded = ({ profileId, fullName, role }) => {
        const invitedProfileId = Number(profileId);
        const invitedRole = Number(role);
        if (!invitedProfileId || !invitedRole) return;

        setTeam((prev) => {
            if (!prev?.team) return prev;
            const currentPlayers = prev.team.players || [];
            const currentCoaches = prev.team.coaches || [];

            if (invitedRole === 2) {
                const alreadyCoach = currentCoaches.some(
                    (member) => Number(member?.playerId) === invitedProfileId
                );
                if (alreadyCoach) return prev;

                return {
                    ...prev,
                    team: {
                        ...prev.team,
                        coaches: [
                            ...currentCoaches,
                            {
                                id: `tmp-coach-${invitedProfileId}`,
                                playerId: invitedProfileId,
                                fullName: fullName || "",
                            },
                        ],
                    },
                };
            }

            const alreadyPlayer = currentPlayers.some(
                (member) => Number(member?.playerId) === invitedProfileId
            );
            if (alreadyPlayer) return prev;

            return {
                ...prev,
                team: {
                    ...prev.team,
                    players: [
                        ...currentPlayers,
                        {
                            id: `tmp-player-${invitedProfileId}`,
                            playerId: invitedProfileId,
                            fullName: fullName || "",
                            shirtNumber: null,
                            status: null,
                            position: null,
                        },
                    ],
                },
            };
        });
    };

    return (
        <>
            <div className="content-wrapper">
                <NavBar navBarElem={navBarElem} />

                <section className="content">
                    <div className="container-fluid">
                        {team && division && activeSeasonData ? (
                            <>
                                <TeamHeader
                                    team={team}
                                    activeSeasonData={activeSeasonData}
                                    division={division}
                                    colors={colors}
                                />
                                <InviteTeamLink
                                    organizationId={team.teamId}
                                    seasonId={activeSeasonData?.id}
                                    isManager={true}
                                />
                                <TeamCoaches
                                    coaches={team?.team?.coaches || []}
                                    colors={colors}
                                    onAddCoach={openAddCoachModal}
                                    onCoachDeleted={handleCoachDeleted}
                                />
                                <PlayersList
                                    team={team}
                                    colors={colors}
                                    division={division}
                                    onPlayerUpdated={handlePlayerUpdated}
                                    onAddPlayer={openAddPlayerModal}
                                    onPlayerDeleted={handlePlayerDeleted}
                                />
                            </>
                        ) : <NoData />}
                    </div>
                </section>
            </div>

            <AddTeamMemberModal
                show={!!inviteRole}
                onClose={closeInviteModal}
                role={inviteRole}
                profileId={activeProfile?.id}
                teamId={team?.teamId}
                seasonId={activeSeasonData?.id}
                existingProfileIds={existingProfileIds}
                colors={colors}
                onInvited={handleMemberAdded}
                onCreateNew={() => {
                    if (!requireEnterpriseUserManagement()) return;
                    setCreateUserRole(inviteRole);
                    setInviteRole(null);
                }}
            />
            <CreateOrganizationUserModal
                show={!!createUserRole}
                onClose={closeCreateUserModal}
                organizationId={division?.organization?.id}
                teamId={team?.teamId}
                seasonId={activeSeasonData?.id}
                role={createUserRole}
                colors={colors}
                onCreated={({profileId, fullName}) => handleMemberAdded({profileId, fullName, role: createUserRole})}
            />
        </>
    );
}

export default TeamDetailPage;
